import { useCallback, useMemo, useState } from 'react';

import { LineChart } from '@/components/charts/LineChart';
import { ToggleSwitch } from '@/components/legacy-ui/ToggleSwitch';
import { Loader } from '@/components/Loader';
import { Tooltip } from '@/components/Tooltip';
import { StatusChip } from '@/components/ui/status-chip';
import {
  useGetConfiguration,
  useUpdateConfiguration,
} from '@/hooks/useConfiguration';
import { useMy } from '@/hooks/useMy';
import { type EvmScoreData } from '@/sections/customerManagement/hooks/data/useCustomerEvmScores';
import { getEvmThreshold } from '@/sections/customerManagement/utils/evmThresholds';
import { getDaysAgo } from '@/sections/insights/metrics/utils/dateUtils';
import { type CustomerTypeValue } from '@/types';

interface EvmScoreDataExtended extends EvmScoreData {
  pasPlatformCloseout: number;
  pasContinuousRecommended: number;
}

function ScoreBar({
  label,
  value,
  max,
  tooltip,
  barClassName = 'bg-chariot-brand-primary',
}: {
  label: string;
  value: number;
  max: number;
  tooltip?: string;
  barClassName?: string;
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-chariot-content-secondary w-24 shrink-0 text-xs">
        {label}
      </span>
      <Tooltip title={tooltip ?? `${value.toFixed(2)} / ${max.toFixed(1)}`}>
        <div className="bg-chariot-layer-1 flex h-2 flex-1 overflow-hidden rounded-full">
          <div
            className={`${barClassName} rounded-full transition-all duration-300`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </Tooltip>
      <span className="w-10 shrink-0 text-right text-xs tabular-nums">
        {value.toFixed(1)}
      </span>
    </div>
  );
}

function formatDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoString;
  }
}

interface EvmScoreBreakdownProps {
  email: string;
  customerType?: CustomerTypeValue;
}

export function EvmScoreBreakdown({
  email,
  customerType,
}: EvmScoreBreakdownProps) {
  const showManualGates =
    customerType === 'PILOT' || customerType === 'ENGAGEMENT';

  const configQuery = useGetConfiguration({ email, disableCache: true });
  const updateConfig = useUpdateConfiguration({ email });

  const { platformCloseout, continuousRecommended, dogpileConfig } =
    useMemo(() => {
      const configs = configQuery.data ?? [];
      let closeout = false;
      let continuous = false;
      let dogpile: { startedAt: string; endsAt: string } | null = null;

      for (const cfg of configs) {
        if (cfg.name === 'evm#platform-closeout') {
          closeout = cfg.value === true || cfg.value === 'true';
        }
        if (cfg.name === 'evm#continuous-recommended') {
          continuous = cfg.value === true || cfg.value === 'true';
        }
        if (cfg.name === 'evm#dogpile' && cfg.value) {
          dogpile = cfg.value as { startedAt: string; endsAt: string };
        }
      }
      return {
        platformCloseout: closeout,
        continuousRecommended: continuous,
        dogpileConfig: dogpile,
      };
    }, [configQuery.data]);

  const dogpileActive = useMemo(() => {
    if (!dogpileConfig?.endsAt) return false;
    return new Date(dogpileConfig.endsAt) > new Date();
  }, [dogpileConfig]);

  const [dogpileEndsAt, setDogpileEndsAt] = useState('');

  const handleToggle = useCallback(
    (name: string, value: boolean) => {
      updateConfig.mutate({ name, value });
    },
    [updateConfig]
  );

  const handleStartDogpile = useCallback(() => {
    if (!dogpileEndsAt) return;
    const endDate = new Date(dogpileEndsAt + 'T23:59:00Z');
    updateConfig.mutate({
      name: 'evm#dogpile',
      value: {
        startedAt: new Date().toISOString(),
        endsAt: endDate.toISOString(),
      },
    });
    setDogpileEndsAt('');
  }, [dogpileEndsAt, updateConfig]);

  const handleCancelDogpile = useCallback(() => {
    updateConfig.mutate({ name: 'evm#dogpile', value: {} });
  }, [updateConfig]);

  const evmQuery = useMy(
    { resource: 'statistic', query: 'evm#score#now' },
    {
      email,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const historyQuery = useMy(
    {
      resource: 'statistic',
      query: `evm#score#${getDaysAgo(90)}`,
      to: `#statistic#evm#score#now&`,
      desc: true,
    },
    {
      email,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const data = useMemo<EvmScoreDataExtended | null>(() => {
    const stats = evmQuery.data ?? [];
    if (stats.length === 0) return null;
    const item = stats[0];
    const values = item.data?.values ?? {};
    const counts = item.data?.counts ?? {};
    return {
      evm: values.evm ?? 0,
      dvs: values.dvs ?? 0,
      pas: values.pas ?? 0,
      dvsRawValue: values.dvs_raw_value ?? 0,
      dvsDecayedValue: values.dvs_decayed_value ?? 0,
      dvsProgress: values.dvs_progress ?? 0,
      pasIntegrations: values.pas_integrations ?? 0,
      pasLogin: values.pas_login ?? 0,
      pasSeedProcessing: values.pas_seed_processing ?? 0,
      pasRiskTriage: values.pas_risk_triage ?? 0,
      pasPlatformCloseout: values.pas_platform_closeout ?? 0,
      pasContinuousRecommended: values.pas_continuous_recommended ?? 0,
      engagementDays: counts.engagement_days ?? 0,
      elapsedDays: counts.elapsed_days ?? 0,
      integrationCount: counts.integration_count ?? 0,
    };
  }, [evmQuery.data]);

  const trendData = useMemo(() => {
    const stats = historyQuery.data ?? [];

    const byDate = new Map<
      string,
      {
        name: string;
        EVM?: number | null;
        DVS?: number | null;
        PAS?: number | null;
      }
    >();
    for (const stat of stats) {
      if (!stat.created || stat.created === 'now') continue;
      const dateKey = stat.created.split('T')[0];
      const label = formatDate(stat.created);
      const values = stat.data?.values ?? {};
      if (!byDate.has(dateKey)) {
        byDate.set(dateKey, {
          name: label,
          EVM:
            typeof values.evm === 'number' && Number.isFinite(values.evm)
              ? values.evm
              : null,
          DVS:
            typeof values.dvs === 'number' && Number.isFinite(values.dvs)
              ? values.dvs
              : null,
          PAS:
            typeof values.pas === 'number' && Number.isFinite(values.pas)
              ? values.pas
              : null,
        });
      }
    }

    const currentStat = evmQuery.data?.[0];
    const currentValues = currentStat?.data?.values ?? {};
    const currentDate = new Date().toISOString();
    const currentDateKey = currentDate.split('T')[0];
    byDate.set(currentDateKey, {
      name: formatDate(currentDate),
      EVM:
        typeof currentValues.evm === 'number' &&
        Number.isFinite(currentValues.evm)
          ? currentValues.evm
          : null,
      DVS:
        typeof currentValues.dvs === 'number' &&
        Number.isFinite(currentValues.dvs)
          ? currentValues.dvs
          : null,
      PAS:
        typeof currentValues.pas === 'number' &&
        Number.isFinite(currentValues.pas)
          ? currentValues.pas
          : null,
    });

    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, value]) => value);
  }, [historyQuery.data, evmQuery.data]);

  if (evmQuery.status === 'pending') {
    return <Loader isLoading className="h-20" />;
  }

  if (!data) return null;

  const threshold = getEvmThreshold(data.evm);
  const pasMax = showManualGates ? 10 / 6 : 2.5; // 10 / (4 base + 2 manual gates for PILOT/ENGAGEMENT)

  return (
    <div className="border-chariot-stroke-default bg-chariot-layer-0 space-y-4 rounded-lg border p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-chariot-content-primary text-sm font-medium">
            EVM Score
          </span>
          <StatusChip
            label={threshold.label}
            variant={threshold.variant}
            tooltip={`${threshold.label} (${data.evm.toFixed(1)}/10)`}
          />
        </div>
        <span className="text-chariot-content-primary text-2xl font-bold tabular-nums">
          {data.evm.toFixed(1)}
          <span className="text-chariot-content-secondary text-sm font-normal">
            /10
          </span>
        </span>
      </div>

      {/* Trend Chart */}
      {trendData.length > 1 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <div className="text-chariot-content-secondary flex items-center gap-2">
              <span className="bg-chariot-brand-primary h-1 w-6 rounded-full" />
              EVM
            </div>
            <div className="text-chariot-content-secondary flex items-center gap-2">
              <span className="bg-chariot-stroke-high h-1 w-6 rounded-full" />
              DVS
            </div>
            <div className="text-chariot-content-secondary flex items-center gap-2">
              <span className="bg-chariot-stroke-info h-1 w-6 rounded-full" />
              PAS
            </div>
          </div>
          <div className="h-48">
            <LineChart
              type="monotone"
              data={trendData}
              showTooltip={false}
              lines={[
                {
                  dataKey: 'EVM',
                  dotClassName: 'stroke-chariot-brand-primary',
                  lineClassName:
                    '[&>path]:stroke-chariot-brand-primary text-chariot-brand-primary',
                  stroke: 'rgb(var(--chariot-brand-primary))',
                },
                {
                  dataKey: 'DVS',
                  dotClassName: 'stroke-chariot-stroke-high',
                  lineClassName:
                    '[&>path]:stroke-chariot-stroke-high text-chariot-stroke-high',
                  stroke: 'rgb(var(--chariot-stroke-high))',
                },
                {
                  dataKey: 'PAS',
                  dotClassName: 'stroke-chariot-stroke-info',
                  lineClassName:
                    '[&>path]:stroke-chariot-stroke-info text-chariot-stroke-info',
                  stroke: 'rgb(var(--chariot-stroke-info))',
                },
              ]}
              yAxisProps={{ domain: [0, 10], ticks: [0, 2, 4, 6, 8, 10] }}
              tooltipFormatter={(value, name) => [
                Number(value).toFixed(1),
                name,
              ]}
              margin={{ top: 12, right: 8, bottom: 0, left: -20 }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* DVS Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-chariot-content-secondary text-xs font-semibold tracking-wider uppercase">
              DVS (Delivery Value)
            </span>
            <span className="text-sm font-medium tabular-nums">
              {data.dvs.toFixed(1)}
            </span>
          </div>

          <ScoreBar
            label="Raw findings"
            value={data.dvsRawValue}
            max={Math.max(data.dvsRawValue, 50)}
            tooltip={`Severity-weighted finding value: ${data.dvsRawValue.toFixed(1)}`}
            barClassName="bg-chariot-stroke-high"
          />
          <ScoreBar
            label="Decayed"
            value={data.dvsDecayedValue}
            max={Math.max(data.dvsRawValue, 50)}
            tooltip={`After time decay: ${data.dvsDecayedValue.toFixed(1)} (raw: ${data.dvsRawValue.toFixed(1)})`}
            barClassName="bg-chariot-stroke-high"
          />

          <div className="flex items-center gap-2 pt-1">
            <span className="text-chariot-content-secondary w-24 shrink-0 text-xs">
              Progress
            </span>
            <Tooltip
              title={`Day ${data.elapsedDays} of ${data.engagementDays}`}
            >
              <div className="bg-chariot-layer-1 flex h-2 flex-1 overflow-hidden rounded-full">
                <div
                  className="bg-chariot-stroke-medium rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(100, data.dvsProgress * 100)}%`,
                  }}
                />
              </div>
            </Tooltip>
            <span className="w-10 shrink-0 text-right text-xs tabular-nums">
              {Math.round(data.dvsProgress * 100)}%
            </span>
          </div>
        </div>

        {/* PAS Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-chariot-content-secondary text-xs font-semibold tracking-wider uppercase">
              PAS (Platform Adoption)
            </span>
            <span className="text-sm font-medium tabular-nums">
              {data.pas.toFixed(1)}
            </span>
          </div>

          <ScoreBar
            label="Integrations"
            value={data.pasIntegrations}
            max={pasMax}
            tooltip={`${data.integrationCount} integrations configured`}
            barClassName="bg-chariot-stroke-info"
          />
          <ScoreBar
            label="Logins"
            value={data.pasLogin}
            max={pasMax}
            tooltip="Customer login activity (distinct users + frequency)"
            barClassName="bg-chariot-stroke-info"
          />
          <ScoreBar
            label="Seed Processing"
            value={data.pasSeedProcessing}
            max={pasMax}
            tooltip="Ratio of processed seeds to total seeds"
            barClassName="bg-chariot-stroke-info"
          />
          <ScoreBar
            label="Risk Triage"
            value={data.pasRiskTriage}
            max={pasMax}
            tooltip="Ratio of triaged critical/high risks"
            barClassName="bg-chariot-stroke-info"
          />
          {showManualGates && (
            <>
              <ScoreBar
                label="Closeout"
                value={(data as EvmScoreDataExtended).pasPlatformCloseout ?? 0}
                max={pasMax}
                tooltip="Platform used for closeout presentation"
                barClassName="bg-chariot-stroke-info"
              />
              <ScoreBar
                label="Continuous"
                value={
                  (data as EvmScoreDataExtended).pasContinuousRecommended ?? 0
                }
                max={pasMax}
                tooltip="Continuous/subscription included in recommendations"
                barClassName="bg-chariot-stroke-info"
              />
            </>
          )}
        </div>
      </div>

      {/* Manual Gates (PILOT/ENGAGEMENT only) */}
      {showManualGates && (
        <div className="border-chariot-stroke-default space-y-3 border-t pt-4">
          <span className="text-chariot-content-secondary text-xs font-semibold tracking-wider uppercase">
            Manual Gates
          </span>
          <div className="flex items-center justify-between">
            <span className="text-chariot-content-primary text-sm">
              Platform used for closeout
            </span>
            <ToggleSwitch
              checked={platformCloseout}
              onChange={checked =>
                handleToggle('evm#platform-closeout', checked)
              }
              ariaLabel="Platform used for closeout"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-chariot-content-primary text-sm">
              Continuous included in recommendations
            </span>
            <ToggleSwitch
              checked={continuousRecommended}
              onChange={checked =>
                handleToggle('evm#continuous-recommended', checked)
              }
              ariaLabel="Continuous included in recommendations"
            />
          </div>
        </div>
      )}

      {/* Dogpile Management */}
      <div className="border-chariot-stroke-default space-y-3 border-t pt-4">
        <span className="text-chariot-content-secondary text-xs font-semibold tracking-wider uppercase">
          Dogpile Review
        </span>
        {dogpileActive && dogpileConfig ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-md bg-blue-50 p-3 dark:bg-blue-950">
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Dogpile in progress
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Ends{' '}
                  {new Date(dogpileConfig.endsAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              <button
                onClick={handleCancelDogpile}
                className="text-xs font-medium text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-chariot-content-secondary mb-1 block text-xs">
                Dogpile end date
              </label>
              <input
                type="date"
                value={dogpileEndsAt}
                onChange={e => setDogpileEndsAt(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="border-chariot-stroke-default bg-chariot-layer-1 text-chariot-content-primary w-full rounded-md border px-3 py-1.5 text-sm"
              />
            </div>
            <button
              onClick={handleStartDogpile}
              disabled={!dogpileEndsAt}
              className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              Start Dogpile
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
